import { Button, Icons, useClickOutside, useToggle } from '@commenda/kakashi';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { printDateString } from '@commenda/utils';
import { ChatRoomMessageType } from '@prisma/client';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import ChatDateDivider from '../../../chat/components/ChatDateDivider';
import ChatMessageText from '../../../chat/components/ChatMessages/ChatMessageText';
import { AllChatRooms } from '../../../chat/ChatRoomList';
import { useChatMessages } from '../../../hooks/useChatMessages';

const ChatSidebar = ({
  workRequestData,
  userId,
}: {
  workRequestData: WorkRequestResponseType | undefined;
  userId?: string;
}) => {
  const router = useRouter();
  const { messages, isLoading, error, chatRoomId } =
    useChatMessages(workRequestData);

  const chatVisibility = useToggle(false);

  // Handle click outside to close chat
  const handleClickOutside = useCallback(() => {
    if (chatVisibility.value) {
      chatVisibility.setValue(false);
    }
  }, [chatVisibility]);

  const containerRef = useClickOutside<HTMLDivElement>(handleClickOutside);

  // Get service request ID from work request data
  const serviceRequestId =
    workRequestData?.task?.taskGroup?.serviceRequestId;

  // Handle navigation to service request with chat open
  const handleGoToChat = useCallback(() => {
    if (serviceRequestId) {
      router.replace(`/service-request/${serviceRequestId}?openChat=true`);
    } else {
      // Fallback to opening the modal if no service request is linked
      chatVisibility.toggle();
    }
  }, [serviceRequestId, router, chatVisibility]);

  const filteredMessages = useMemo(() => {
    return messages
      .filter(
        (message) =>
          message.type !== ChatRoomMessageType.DOCUMENT_REQUEST &&
          !message.isInternalNoteMessage
      )
      .reverse();
  }, [messages]);

  const hasIssueReported = useMemo(() => {
    return messages.some(
      (msg) => msg.type === ChatRoomMessageType.ISSUE_REPORTED
    );
  }, [messages]);

  const status = workRequestData?.status;

  // Group messages by date
  const messagesWithDividers = useMemo(() => {
    const elements: JSX.Element[] = [];
    let currentDate = filteredMessages?.[0]
      ? printDateString(filteredMessages[0].sentAt)
      : '';

    if (filteredMessages && filteredMessages.length !== 0) {
      elements.push(
        <ChatDateDivider
          key={`divider-start-${filteredMessages[0].id}`}
          date={filteredMessages[0].sentAt}
        />
      );
    }

    filteredMessages?.forEach((message) => {
      const messageDate = printDateString(message.sentAt);

      if (messageDate !== currentDate) {
        currentDate = messageDate;
        elements.push(
          <ChatDateDivider
            key={`divider-${message.id}`}
            date={message.sentAt}
          />
        );
      }

      elements.push(
        <div key={message.id} className="mb-6">
          <ChatMessageText
            isIssueReported={
              message.type === ChatRoomMessageType.ISSUE_REPORTED
            }
            message={message}
            onFileDownload={async () => ({ signedUrl: '' })}
            userId={userId || ''}
            corporationId={workRequestData?.corporationId}
            removeTooltips
          />
        </div>
      );
    });

    return elements;
  }, [filteredMessages, userId]);

  // Don't show sidebar if conditions aren't met
  if (
    !hasIssueReported ||
    isLoading ||
    error ||
    filteredMessages.length === 0 ||
    status === 'Submitted'
  ) {
    return null;
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Enterprise Messages interface */}
      {chatRoomId && userId && chatVisibility.value && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-20">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => chatVisibility.setValue(false)}
          />
          <div className="relative">
            <AllChatRooms
              open={chatVisibility.value}
              chatRoomId={chatRoomId}
              userId={userId}
              companyId={workRequestData?.companyId}
              disableChatBackButton
            />
          </div>
        </div>
      )}

      <div className="w-[440px] bg-white border-l border-gray-200 flex-shrink-0 flex flex-col h-full">
        <div className="flex-1 overflow-y-auto pt-6">
          {messagesWithDividers}
        </div>

        <div className="border-t border-gray-200 bg-white flex-shrink-0">
          <div className="px-6 py-4">
            <div className="flex justify-end">
              <Button
                variant="secondary"
                onClick={handleGoToChat}
                className="shadow-sm"
              >
                Go to chat
                <Icons.ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatSidebar;
